import styled, { css } from 'styled-components'

import { TruncateWithTooltip, Link, Tooltip } from '@sketch/components'
import { Image as ImageUnstyled } from '@sketch/modules-common'
import { VersionLink } from '../../../../../../versioning'
import { ReactComponent as ChevronRight } from '@sketch/icons/chevron-right-17'
import { ReactComponent as ExclamationMark } from '@sketch/icons/exclamation-mark-triangle-17'

import { Header } from '../../components'

const componentStyles = css(
  ({ theme }) => css`
    flex: auto;
    min-width: 0;

    display: flex;
    align-items: center;

    padding-bottom: 8px;
    margin: 4px 20px 0;

    color: ${theme.colors.foreground.secondary.A};
    font-weight: 400;

    &[data-testid='inspector-component-no-link'] {
      margin-left: 0;
    }

    ${Header} > &[data-testid="inspector-component-no-link"] {
      margin-left: 20px;
    }
  `
)

export const Container = styled.div`
  ${componentStyles}
`

export const ContainerWithoutLink = styled.div`
  ${componentStyles};
  margin-right: 0;
`

export const LinkStyled = styled(Link)`
  ${componentStyles}
`

export const VersionLinkStyled = styled(VersionLink)`
  ${componentStyles}
`

export const Body = styled.div`
  display: flex;
  flex-direction: column;

  min-width: 0;
  line-height: 1;
  margin-top: 4px;
`

export const Chevron = styled(ChevronRight)`
  flex: none;
  height: 17px;
  margin-left: auto;
  padding-left: 8px;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

export const Preview = styled(ImageUnstyled)<{
  needsContrastingBackground: boolean
  isTextStyle: boolean
}>(
  ({ needsContrastingBackground, isTextStyle, theme }) => css`
    position: relative;
    flex: none;

    width: 32px;
    height: 32px;

    box-shadow: 0 1px 2px 0 ${theme.colors.shadow.outer};

    margin-right: 8px;
    border-radius: ${theme.radii.base};
    background-color: ${needsContrastingBackground && isTextStyle
      ? theme.colors.background.tertiary.A
      : theme.colors.background.tertiary.C};

    &:after {
      content: '';
      display: ${isTextStyle ? 'block' : 'none'};
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 5px;
    }

    img {
      object-fit: cover;
      object-position: left;
    }
  `
)

export const Name = styled(TruncateWithTooltip)<{ isDirty?: boolean }>(
  ({ theme, isDirty }) => css`
    color: ${theme.colors.foreground.secondary.A};
    font-size: ${theme.fontSizes.D};
    font-style: ${isDirty ? 'italic' : 'normal'};
    height: 16px;
  `
)

export const Path = styled(TruncateWithTooltip)(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    font-size: ${theme.fontSizes.B};
    font-weight: ${theme.fontWeights.medium};
    margin-top: 2px; /* stylelint-disable-line scales/space */
  `
)

export const DirtyLayerWarningTooltipHeader = styled.span`
  color: ${({ theme }) => theme.colors.ui.tooltips.title};
`

export const DirtyLayerWarningTooltipContent = styled.span`
  color: ${({ theme }) => theme.colors.ui.tooltips.foreground};
`

export const ExclamationMarkIcon = styled(ExclamationMark)`
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

// Make the container element of the tooltip content flex so that the
// container uses the same height as its content (the warning icon).
export const StyledDirtyLayerTooltip = styled(Tooltip)`
  display: flex;
`
